import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * The campaign filter — one searchable dropdown, shared by all four tabs.
 *
 * It replaces the chip row. Chips scale to the mock's three campaigns and no
 * further: a real account puts twenty of them in a wrapping band of near-equal
 * buttons, and the Moments tab had grown a SECOND filter vocabulary (advertiser
 * folders) because its chip row was the only place a grouping could live. One
 * dropdown, campaign-level everywhere, with search for the accounts where the
 * list is long — the same control on every tab, so "filter by campaign" means
 * one thing across the section.
 *
 * Every option carries its COUNT, computed by the caller over the same set the
 * selection will show — the dropdown promises what clicking delivers, which is
 * the rule the chips already followed. 'all' is not an option the caller
 * passes; it is the control's own first row, with `allCount` behind it.
 *
 * THE SELECTED OPTION CAN DISAPPEAR UNDERNEATH THE SELECTION — dismiss the last
 * card of a campaign and its option goes while the stored key survives, which
 * used to empty the feed against a filter no longer on offer. The control
 * resolves a vanished key to 'all' itself (and reports what it resolved through
 * onChange's read side, `live`), so every caller inherits the fix rather than
 * each carrying its own copy — MomentsTab had one, CardFeed did not.
 */
export default function CampaignFilter({
  value,
  multiple = false,
  onChange,
  options = [],          // [{ key, label, count }]
  allCount = 0,
  allLabel = 'All',
  searchPlaceholder = 'Search campaigns…',
  name = 'Campaign',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const live = value !== 'all' && options.some((o) => o.key === value) ? value : 'all';
  const selected = multiple && Array.isArray(value) ? value : [];
  const current = multiple ? { label: selected.length ? `${selected.length} ${selected.length === 1 ? 'campaign' : 'campaigns'} selected` : allLabel, count: selected.length ? options.filter((o) => selected.includes(o.key)).reduce((sum, o) => sum + o.count, 0) : allCount } : live === 'all'
    ? { label: allLabel, count: allCount }
    : options.find((o) => o.key === live);

  // Busiest first, then A→Z — the order the counts make useful. Sorted here so
  // the tabs agree without each remembering to.
  const ordered = useMemo(
    () => [...options].sort((a, b) => (b.count || 0) - (a.count || 0)
      || String(a.label || '').localeCompare(String(b.label || ''))),
    [options],
  );

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return ordered;
    return ordered.filter((o) => String(o.label || '').toLowerCase().includes(needle));
  }, [ordered, q]);

  // Outside click and Escape close; both listeners exist only while open.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A fresh search per open — a leftover query silently hiding options is a
  // filter the reader never chose.
  useEffect(() => {
    if (open) {
      setQ('');
      inputRef.current?.focus();
    }
  }, [open]);

  const pick = (key) => {
    if (multiple) {
      onChange(key === 'all' ? [] : selected.includes(key) ? selected.filter((id) => id !== key) : [...selected, key]);
    } else { onChange(key); setOpen(false); }
  };

  return (
    <div className="cfd" ref={rootRef}>
      <button
        type="button"
        className={`cfd-btn${open ? ' open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${name} filter: ${current?.label ?? allLabel}`}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="cfd-cur">{current?.label ?? allLabel}</span>
        <b className="cfd-n">{current?.count ?? 0}</b>
        <span aria-hidden="true" className="material-icons cfd-chev">
          {open ? 'expand_less' : 'expand_more'}
        </span>
      </button>

      {open ? (
        <div className="cfd-pop">
          <div className="cfd-search">
            <span aria-hidden="true" className="material-icons">search</span>
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
            />
          </div>
          <div role="listbox" aria-multiselectable={multiple || undefined} aria-label={name} className="cfd-list">
            {/* 'All' stays visible under any search — it is the way back out. */}
            <button
              type="button"
              role="option"
              aria-selected={multiple ? !selected.length : live === 'all'}
              className={`cfd-opt${(multiple ? !selected.length : live === 'all') ? ' on' : ''}`}
              onClick={() => pick('all')}
            >
              <span className="cfd-opt-l">{allLabel}</span>
              <b className="cfd-n">{allCount}</b>
            </button>
            {shown.map((o) => (
              <button
                key={o.key}
                type="button"
                role="option"
                aria-selected={multiple ? selected.includes(o.key) : live === o.key}
                className={`cfd-opt${(multiple ? selected.includes(o.key) : live === o.key) ? ' on' : ''}`}
                onClick={() => pick(o.key)}
              >
                <span className="cfd-opt-l">{o.label}</span>
                <b className="cfd-n">{o.count}</b>
              </button>
            ))}
            {!shown.length ? (
              <div className="cfd-none">No campaigns match &ldquo;{q}&rdquo;</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
